import { useQuery } from "@apollo/client";
import "handsontable/dist/handsontable.full.min.css";
import { Dispatch, SetStateAction, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Dialog from "src/components/Dialog";
import Icon from "src/components/Icon";
import InnerLoading from "src/components/InnerLoading";
import {
  FilterInvitationList,
  NumberOfGuestsByCategory,
} from "src/graphql/queries";
import useAppStore from "src/store/utils";
import {
  filterInvitationListBy,
  filterInvitationListByVariables,
  invitationListFilter,
  noGuestPerCategory,
} from "src/types/api.d";
import InviteSheet from "./InviteSheet";
import SendInvitesOptionsModal from "./modals/SendInvite";
import "./styles.css";

interface AddGuestsSpreadsheetProps {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const AddGuestsSpreadsheet = ({ setOpen }: AddGuestsSpreadsheetProps) => {
  const { event } = useAppStore((state) => state);
  const navigate = useNavigate();
  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false);

  //categories data
  const { data: categories } = useQuery<noGuestPerCategory>(
    NumberOfGuestsByCategory,
    {
      variables: {
        eventId: event.id,
      },
      fetchPolicy: "cache-and-network",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    }
  );

  //invite list data
  const {
    data: inviteList,
    loading: inviteListLoader,
    refetch,
  } = useQuery<filterInvitationListBy, filterInvitationListByVariables>(
    FilterInvitationList,
    {
      variables: {
        eventId: event.id,
        filter: invitationListFilter.not_sent,
        page: 1,
        pageSize: 1000,
      },
      skip: !event.id,
      fetchPolicy: "network-only",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    }
  );

  //invitation ids
  const invitationIDs =
    inviteList?.filterInvitationListBy?.map((item) => item?.uuid ?? "") ?? [];

  return (
    <div className="pb-8">
      {categories && categories?.noGuestPerCategory?.length === 0 ? (
        <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
          <h1 className="text-xl font-bold">
            You currently have no category added, please add categories
          </h1>
          <div>
            <Button
              label="add row"
              type="button"
              design="primary"
              onClick={() => navigate(`/invitations?tab=categories`)}
              className="flex items-center gap-[0.7rem] !text-white"
            >
              Add category
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-5 flex justify-between">
            <div
              className="pointer flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-all duration-300 ease-out hover:bg-[#f2f2f2]"
              onClick={() => setOpen(false)}
            >
              <div>
                <Icon iconName="backArrow" />
              </div>
              <span>Go Back</span>
            </div>
            <div>
              <Dialog
                open={openInviteModal}
                onOpenChange={setOpenInviteModal}
                trigger={
                  <Button
                    label="Send invites"
                    type="button"
                    design="primary"
                    className="flex items-center gap-[0.7rem] !text-white"
                  >
                    Send Invites
                  </Button>
                }
              >
                <SendInvitesOptionsModal
                  invitationFilter={invitationListFilter.not_sent}
                  noOfGuests={inviteList?.filterInvitationListBy?.length ?? 0}
                  invitationIDs={invitationIDs}
                  setOpenInviteModal={setOpenInviteModal}
                />
              </Dialog>
            </div>
          </div>
          <div>
            {inviteListLoader ? (
              <div>
                <InnerLoading />
              </div>
            ) : (
              <InviteSheet inviteList={inviteList} refetch={refetch} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddGuestsSpreadsheet;
